import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp, RevealLinesOnView } from "@/components/motion/Reveals";
import CountUp from "@/components/motion/CountUp";
import { useLang } from "@/i18n/LangContext";
import { useContent, resolveUrl } from "@/cms/ContentContext";
import { useLeafTx } from "@/cms/useTexts";
import { useSpacing } from "@/cms/useSpacing";

const Sobre = () => {
  const { t, lang } = useLang();
  const { content } = useContent();
  const tx = useLeafTx();
  const { sectionPy, titleGap, overlineGap } = useSpacing();
  const PORTRAIT = resolveUrl(content?.images?.sobre) || "";
  const hasPortrait = !!PORTRAIT;
  const sobre = content?.sobre;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);

  const p1 = sobre ? (lang === "pt" ? sobre.p1_pt : sobre.p1_en) : t.sobre.p1;
  const p2 = sobre ? (lang === "pt" ? sobre.p2_pt : sobre.p2_en) : t.sobre.p2;
  const stats = sobre
    ? [
        { n: sobre.stat1_n, l: lang === "pt" ? sobre.stat1_l_pt : sobre.stat1_l_en },
        { n: sobre.stat2_n, l: lang === "pt" ? sobre.stat2_l_pt : sobre.stat2_l_en },
        { n: sobre.stat3_n, l: lang === "pt" ? sobre.stat3_l_pt : sobre.stat3_l_en },
      ]
    : [t.sobre.stat1, t.sobre.stat2, t.sobre.stat3];

  return (
    <section
      id="sobre"
      ref={ref}
      data-testid="sobre-section"
      className={`relative ${sectionPy("sobre")}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-12">
        {/* Left – image (only if uploaded) */}
        {hasPortrait && (
          <div className="col-span-12 lg:col-span-5 relative">
            <div className="relative aspect-[3/4] overflow-hidden frame-clip border border-[#3A3524]/12">
              <motion.img
                src={PORTRAIT}
                alt="Isa"
                style={{ y }}
                className="absolute inset-0 w-full h-[120%] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 md:-right-10 bg-[#F5F2EC] px-4 py-3 border border-[#3A3524]/12">
              <div className="overline text-[#6B6554]">Isa</div>
            </div>
          </div>
        )}

        {/* Right – text */}
        <div className={`col-span-12 ${hasPortrait ? "lg:col-span-7 lg:pl-8" : "lg:col-span-12"} flex flex-col justify-start`}>
          <FadeUp>
            <div className={`overline overline-2x text-[#6B6554] ${overlineGap("sobre")}`}>{tx("sobre.overline", t.sobre.overline)}</div>
          </FadeUp>

          <FadeUp delay={0.15}>
            <p className="text-[#3A3524]/85 text-lg leading-relaxed mb-4 max-w-xl text-justify font-light">
              {p1}
            </p>
          </FadeUp>
          <FadeUp delay={0.25}>
            <p className="text-[#3A3524]/70 text-base leading-relaxed max-w-xl text-justify font-light">
              {p2}
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default Sobre;
